import { Link } from 'react-router-dom';
import { MapPin, Crown, Users, Activity, Calendar } from 'lucide-react';

const SPORT_GRADIENTS = {
  Football: 'linear-gradient(135deg, #10b981 0%, #f59e0b 100%)',
  Cricket: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  Badminton: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 100%)',
  Tennis: 'linear-gradient(135deg, #84cc16 0%, #06b6d4 100%)',
  Basketball: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
  Pickleball: 'linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)',
  Volleyball: 'linear-gradient(135deg, #eab308 0%, #06b6d4 100%)',
  Hockey: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
};

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

// Squad card with team-identity aesthetic — football-club / esports vibes.
export default function ArenaxSquadCard({ squad, dense = false }) {
  const grad = SPORT_GRADIENTS[squad.primary_sport] || 'var(--ax-primary-gradient)';
  return (
    <Link to={`/arenax/squads/${squad.id}`} data-testid={`arenax-squad-card-${squad.id}`}
      className="group block rounded-2xl overflow-hidden border border-slate-800 hover:border-cyan-500/40 transition-all hover:-translate-y-1"
      style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.04), var(--ax-card-bg-soft))' }}>
      {/* Team identity banner */}
      <div className="relative h-32 overflow-hidden" style={{ background: grad }}>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.18), transparent 60%)',
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 16px)',
        }} />
        {/* Sport emblem */}
        <div className="absolute top-3 right-3 text-6xl opacity-30">
          {SPORT_ICONS[squad.primary_sport] || '🏟️'}
        </div>
        {/* Squad name overlaid */}
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/85 font-bold mb-1">
            {squad.primary_sport} · {squad.skill_level}
          </div>
          <div className="text-xl font-black text-white truncate drop-shadow-lg">
            {squad.name}
          </div>
        </div>
      </div>

      <div className="p-4">
        {!dense && squad.bio && (
          <div className="text-xs text-slate-400 line-clamp-2 mb-3">"{squad.bio}"</div>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{squad.city}</span>
          <span className="flex items-center gap-1"><Crown className="h-3 w-3 text-amber-400" />{squad.captain_name || 'Captain'}</span>
        </div>

        {/* Member stack + capacity */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1.5">
              {Array.from({ length: Math.min(4, squad.members_count || 0) }).map((_, i) => (
                <div key={i} className="h-6 w-6 rounded-full border-2 border-slate-900 flex items-center justify-center text-[9px] font-bold text-slate-950"
                  style={{ background: ['#06b6d4', '#f59e0b', '#ec4899', '#a855f7'][i] }}>
                  {String.fromCharCode(65 + (i * 5) % 26)}
                </div>
              ))}
              {(squad.members_count || 0) > 4 && (
                <div className="h-6 w-6 rounded-full border-2 border-slate-900 bg-slate-700 flex items-center justify-center text-[9px] font-bold text-slate-300">
                  +{squad.members_count - 4}
                </div>
              )}
            </div>
            <span className="text-xs text-slate-400 font-semibold tabular-nums">{squad.members_count}/{squad.max_members}</span>
          </div>
          {squad.next_game && (
            <div className="text-right">
              <div className="text-[9px] tracking-wider uppercase text-amber-400 font-bold flex items-center gap-1">
                <Calendar className="h-3 w-3" />NEXT
              </div>
              <div className="text-xs text-slate-300 font-semibold">{new Date(squad.next_game.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })} {squad.next_game.start_time}</div>
            </div>
          )}
        </div>

        {/* Reputation strip */}
        <div className="flex items-center gap-3 text-[10px] tracking-wider uppercase">
          <div className="flex items-center gap-1 text-emerald-400 font-bold">
            <Activity className="h-3 w-3" />
            <span>{squad.attendance_rate?.toFixed(0) ?? 100}% attendance</span>
          </div>
          <span className="text-slate-700">·</span>
          <div className="text-slate-500 font-semibold">{squad.games_played_total ?? 0} games played</div>
        </div>
      </div>
    </Link>
  );
}
